import * as yoda from '@yoda/bridge';
import EventEmitter from 'eventemitter3';
export enum KimEvent {
  /** 顶部自定义 Button 点击事件 */
  TOP_BAR_BTN_CLICK = 'top-bar-button-click',
  /** 更多菜单自定义功能点击事件 */
  BOTTOM_SHEET_BTN_CLICK = 'kim-web-bottom-sheet-button-click',
}

type IListener = (params: any) => void;

/** 顶部自定义 Button 属性编号 */
export type ITopBarButtonID =
  | 'left1'
  | 'left2'
  | 'right1'
  | 'right2'
  | 'right3';

/** 顶部自定义 Button 属性 */
export interface ITopBarButtonOptions {
  /** Button 编号 */
  id?: ITopBarButtonID;
  /** Button 类型 */
  viewType?: 'imageView' | 'textView';
  /** Button 图标 url */
  image?: string;
  /** 附带数据，json序列化格式*/
  text?: string;
  behavior?: string;
}

/** 首页更多 TapSheet 单项 */
export interface IMarioHomeMoreTapSheetItem {
  business: 'home' | 'chat';
  type: 'event' | 'link' | 'scheme';
  indentify: string;
  title: string;
}

const LISTENERS_WITH_HUB: string[] = [
  KimEvent.BOTTOM_SHEET_BTN_CLICK,
  KimEvent.TOP_BAR_BTN_CLICK,
];

class KimBridge {
  /**
   * @description 添加客户端操作监听事件
   */
  private events = new EventEmitter();
  constructor() {}

  public async setTopBarStylePosition(
    position: 'default' | 'none' | 'fixed' | 'float',
    colorType?: 'light' | 'dark',
    backgroundColor?: string,
  ) {
    const result = await yoda.invoke('ui.setTopBarStyle', {
      position,
      statusBarColorType: colorType,
      backgroundColor,
    } as any);

    return result;
  }

  /**
   * @description 设置 Mario 顶部导航栏样式（参数与 setTopBarStylePosition 一致）
   */
  public async setMarioTopBarStyle(
    position: 'default' | 'none' | 'fixed' | 'float',
    colorType?: 'light' | 'dark',
    backgroundColor?: string,
  ) {
    const result = await yoda.invoke(
      'kim.setMarioTopBarStyle' as any,
      {
        position,
        statusBarColorType: colorType,
        backgroundColor,
      } as any,
    );

    return result;
  }

  /**
   * @description: 打开新的 webview 页面
   * @param {string} url 新的 webview 页面地址
   */
  async openWebview(url: string, options?: any) {
    const { result } = await yoda.invoke('webview.open', {
      url,
      launchOptions: options,
    });
    result !== 1 &&
      console.error('kim.openWebview.result', result, {
        url,
        launchOptions: options,
      });
  }

  // async addListenerToHub(type: string, listener: IListener) {
  //   if (!this.events.has(type)) {
  //     const handlerHub = (params: any) => {
  //       this.events.emit(type, params);
  //     };
  //     // 建立向 kim 的桥
  //     await yoda.addListener(type as any, handlerHub);
  //   }
  //   this.events.on(type, listener);
  // }

  async removeListenerFromHub(type: string, listener?: IListener) {
    if (!listener) {
      this.events.removeAllListeners(type);
    } else {
      this.events.off(type, listener);
    }
  }

  async addListener(type: string, listener: IListener) {
    // if (this.isHubEventType(type)) {
    //   this.addListenerToHub(type, listener);
    // } else {
    try {
      await yoda.addListener(type as any, listener);
    } catch (error) {
      console.warn(
        '[KimBridge] addListener failed (non-Yoda env?):',
        type,
        error,
      );
    }
    // }
  }

  /**
   * @description 移除客户端操作监听事件
   */
  async removeListener(type: string, listener?: IListener) {
    if (this.isHubEventType(type)) {
      this.removeListenerFromHub(type, listener);
    } else {
      await yoda.removeListener(type as any, listener as IListener);
    }
  }

  /**
   * @description: 关闭 webview 页面
   */
  async closeWebview(data?: object) {
    await yoda.invoke('webview.close', data);
  }

  /**
   * @description: 返回或关闭 webview 页面
   */
  async backOrCloseWebview() {
    await yoda.invoke('webview.backOrClose');
  }

  async setTitle(title: string) {
    await yoda.invoke('ui.setTitle', { title: title } as any);
  }

  async setMarioTitle(options: {
    title: string;
    type: 0 | 1 | 2; // 0：首页，1：进行中的会话、历史会话，2：表示清除titleBar
    icon?: string;
  }) {
    await yoda.invoke('kim.setMarioTitle', options);
  }

  public async openMario(options: {
    title?: string;
    type: 0 | 1; // 0：首页，1：进行中的会话、历史会话
    url: string;
    icon?: string;
  }) {
    await yoda.invoke('kim.openMario', options);
  }

  /**
   * @description Kim 顶部自定义 Button
   * @param {ITopBarButtonOptions} option 顶部自定义 Button 属性
   * @param {function} listener 顶部自定义 Button 行为回调
   */
  public async setTopBarButton(
    option: ITopBarButtonOptions,
    listener?: IListener,
  ) {
    const { id = 'right2', viewType = 'imageView', image, ...others } = option;
    try {
      await yoda.invoke('ui.setTopBarButton', {
        id,
        viewType,
        image,
        ...others,
      } as any);
    } catch (error) {
      console.error('kim.setTopBarButton.result');
    }
    listener && this.addListener(KimEvent.TOP_BAR_BTN_CLICK, listener);
  }

  /**
   * @description: 移除 Kim 顶部自定义 Button
   * @param {ITopBarButtonID} id 顶部自定义 Button 属性编号
   * @param {function} listener 顶部自定义 Button 行为回调
   */
  public async removeTopBarButton(
    option?: Partial<ITopBarButtonOptions>,
    listener?: IListener,
  ) {
    const { id = 'right2' } = option || {};
    try {
      await yoda.invoke('ui.removeTopBarButton', { id });
    } catch (error) {
      console.error('kim.removeTopBarButton.result', error, { id });
    }
    listener && this.removeListener(KimEvent.TOP_BAR_BTN_CLICK, listener);
  }

  public async deleteMario(): Promise<boolean> {
    const res: { result: number; data: { isDelete: number } } =
      await yoda.invoke('kim.deleteMario');
    return res.data?.isDelete == 1;
  }

  public async taskPromptDialog(options: {
    maxLimitReached: boolean;
  }): Promise<boolean> {
    // const res: { result: number; data: { isTerminal: number } } =
    await yoda.invoke('kim.taskPromptDialog', options);
    // return res.data?.isTerminal == 1;
  }

  public async showInKim(data: {
    thumb?: string;
    thumbUrl?: string;
    url?: string;
  }) {
    await yoda.invoke('kim.showInKim', {
      type: 'image',
      data,
    });
  }

  public async openKimAlbum() {
    const { data } = await yoda.invoke('kim.openKimAlbum');
    return data;
  }

  public async openDocumentFile(data: {
    // 用于标识下载缓存
    fileIdentity: string;
    fileName: string;
    //下载链接
    fileUrl: string;
    fileSizeBytes: number;
  }) {
    await yoda.invoke('kim.openDocumentFile', data);
  }

  public async shareMarioUrl(url: string) {
    await yoda.invoke('kim.shareMarioUrl', { url });
  }

  public async getKimStatus() {
    const { data = {} } = await yoda.invoke('kim.getKimStatus');
    return data;
  }

  /**
   * @description 设置 Mario 首页右上角「更多」点击后的 TapSheet 列表
   * @param list TapSheet 展示项列表，点击更多后会触发并展示
   */
  public async setMarioHomeMoreTapSheet(list: IMarioHomeMoreTapSheetItem[]) {
    await yoda.invoke('kim.setMarioHomeMoreTapSheet' as any, { list });
  }

  /**
   * @description 设置 Mario 单个会话页右上角「更多」点击后的 TapSheet 列表
   * @param list TapSheet 展示项列表，点击更多后会触发并展示
   */
  public async setMarioChatMoreTapSheet(list: IMarioHomeMoreTapSheetItem[]) {
    await yoda.invoke('kim.setMarioChatMoreTapSheet' as any, { list });
  }

  private isHubEventType(type: string) {
    return LISTENERS_WITH_HUB.includes(type);
  }

  public async openInviteDigitalEmployee(groupId: string) {
    return await yoda.invoke('kim.openInviteDigitalEmployee' as any, { groupId });
  }
}

export const kimBridge = new KimBridge();
